/**
 * Payment Session Service
 *
 * API client for secure payment session management.
 * Used by the PaymentPage when customers open payment links.
 *
 * Features:
 * - Validate payment session tokens
 * - Get session status for polling
 * - Initiate checkout with locked values from token
 *
 * Security:
 * - All locked values (amount, currency, crypto, network) come from the
 *   signed JWT token and cannot be modified by the frontend
 * - Session expiry is validated on every request
 */

import axios from 'axios';
import { formatErrorMessage } from './formatError.js';

// Re-exported for existing importers (e.g. OnrampPayTab).
export { formatErrorMessage };

import { getApiBaseUrl, getEnvironment } from '../config/apiConfig.js';

const ENV = getEnvironment();
const BASE_URL = getApiBaseUrl();

// Log environment on init (only in debug mode)
if (process.env.REACT_APP_ENABLE_DEBUG === 'true') {
  console.log(`[PaymentSessionService] ENV: ${ENV}, API: ${BASE_URL}`);
}

/**
 * Create axios instance with credentials enabled
 * Credentials are needed for cookie-based session tracking (if implemented)
 */
const sessionClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true,
});

/**
 * Response interceptor - normalize API responses
 * Backend returns { status, message, data } format
 */
sessionClient.interceptors.response.use(
  (response) => {
    // Extract data from standardized response format
    if (response.data && response.data.data !== undefined) {
      return response.data.data;
    }
    return response.data;
  },
  (error) => {
    const errorResponse = handleSessionError(error);
    return Promise.reject(errorResponse);
  }
);

/**
 * Handle API errors and return standardized error object
 *
 * @param {Error} error - Axios error object
 * @returns {Object} Standardized error object with type, status, message
 */
const handleSessionError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    return {
      type: status === 401 ? 'session' : 'error',
      status,
      message:
        formatErrorMessage(data?.message) ||
        formatErrorMessage(data?.error) ||
        'An error occurred',
      errorCode: data?.errorCode,
    };
  }

  if (error.request) {
    return {
      type: 'network',
      status: 0,
      message: 'Network error. Please check your connection.',
    };
  }

  return {
    type: 'unknown',
    status: 0,
    message: error.message || 'An unexpected error occurred',
  };
};

/**
 * Payment Session Service class
 * Provides methods for interacting with payment session API endpoints
 */
class PaymentSessionService {
  /**
   * Validate a payment session token
   *
   * Calls the backend to verify the JWT signature and check expiry.
   * Returns session data with locked values (amount, currency, etc.)
   * that the frontend displays but cannot modify.
   *
   * @param {string} token - JWT payment session token from URL
   * @returns {Promise<Object>} ValidateSessionResponse with session data or error
   */
  async validateSession(token) {
    return sessionClient.get(`/api/v1/payment-session/${token}/validate`);
  }

  /**
   * Get current session status
   *
   * Used for polling to check if payment has been completed or expired.
   *
   * @param {string} token - JWT payment session token
   * @returns {Promise<Object>} SessionStatusResponse with status, expired, widgetUrl
   */
  async getSessionStatus(token) {
    return sessionClient.get(`/api/v1/payment-session/${token}/status`);
  }

  /**
   * Initiate checkout with selected provider
   *
   * Validates the token and generates a widget URL using locked values
   * from the JWT token. The amount/currency/crypto/network are taken
   * from the token, not from user input.
   *
   * @param {string} token - JWT payment session token
   * @param {Object} checkoutDto - Checkout options
   * @param {string} checkoutDto.provider - On-ramp provider (transak, banxa)
   * @param {string} [checkoutDto.paymentMethod] - Payment method
   * @param {string} [checkoutDto.walletAddress] - Custom wallet address (optional)
   * @returns {Promise<Object>} CheckoutResponse with widgetUrl
   */
  async initiateCheckout(token, checkoutDto) {
    return sessionClient.post(`/api/v1/payment-session/${token}/checkout`, checkoutDto);
  }
}

/**
 * Session status constants
 * Maps to backend OrderStatus enum
 */
export const SESSION_STATUS = {
  PENDING: 'PENDING',
  AWAITING_PAYMENT: 'AWAITING_PAYMENT',
  PAYMENT_VERIFYING: 'PAYMENT_VERIFYING',
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
};

/**
 * Session error codes
 * Returned by validateSession when token is invalid
 */
export const SESSION_ERROR = {
  EXPIRED: 'EXPIRED',           // Session has expired
  INVALID_TOKEN: 'INVALID_TOKEN', // Token signature invalid
  COMPLETED: 'COMPLETED',       // Payment already completed
  CANCELLED: 'CANCELLED',       // Session was cancelled
};

/**
 * Status display configuration
 * Used for UI rendering of different statuses
 */
export const SESSION_STATUS_CONFIG = {
  [SESSION_STATUS.PENDING]: {
    color: '#f59e0b',
    label: 'Pending',
    icon: 'clock',
  },
  [SESSION_STATUS.AWAITING_PAYMENT]: {
    color: '#3b82f6',
    label: 'Awaiting Payment',
    icon: 'credit-card',
  },
  [SESSION_STATUS.PROCESSING]: {
    color: '#6366f1',
    label: 'Processing',
    icon: 'loader',
  },
  [SESSION_STATUS.COMPLETED]: {
    color: '#10b981',
    label: 'Completed',
    icon: 'check-circle',
  },
  [SESSION_STATUS.FAILED]: {
    color: '#ef4444',
    label: 'Failed',
    icon: 'x-circle',
  },
  [SESSION_STATUS.EXPIRED]: {
    color: '#6b7280',
    label: 'Expired',
    icon: 'clock',
  },
  [SESSION_STATUS.CANCELLED]: {
    color: '#6b7280',
    label: 'Cancelled',
    icon: 'x',
  },
};

// Export singleton instance
export default new PaymentSessionService();
